import { useEffect, useState } from "react";
import { Eye, Download, Heart, Users, Image as ImageIcon, Film } from "lucide-react";
import api, { mediaUrl } from "../../lib/api";

const RANGES = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

const OVERVIEW_CARDS = [
  { key: "views", label: "Views", icon: Eye, color: "#0FFFE2" },
  { key: "downloads", label: "Downloads", icon: Download, color: "#D0FF0D" },
  { key: "favorites", label: "Favorites", icon: Heart, color: "#DF1283" },
  { key: "active_clients", label: "Active Clients", icon: Users, color: "#4A0A66" },
];

const METRICS = [
  { key: "views", label: "Top Viewed", icon: Eye, color: "#0FFFE2" },
  { key: "downloads", label: "Top Downloaded", icon: Download, color: "#D0FF0D" },
  { key: "favorites", label: "Top Favorited", icon: Heart, color: "#DF1283" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

function TopAdsCard({ metric, label, icon: Icon, color, range }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api
      .get("/admin/analytics/top-ads", { params: { range, metric, limit: 10 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [metric, range]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`top-ads-${metric}`}>
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
          <Icon size={15} style={{ color: color === "#D0FF0D" ? "#000000" : color }} />
        </div>
        <h3 className="font-display font-black text-lg text-[#000000]">{label}</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {items === null ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400" data-testid={`top-ads-${metric}-empty`}>
            No activity yet in this range.
          </div>
        ) : (
          items.map((it, idx) => (
            <div key={it.ad_id} className="flex items-center gap-4 px-6 py-3" data-testid={`top-ads-${metric}-row-${idx}`}>
              <div className="w-6 text-sm font-bold text-gray-400 tabular-nums">{idx + 1}</div>
              <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                {it.has_thumbnail ? (
                  <img src={mediaUrl(it.ad_id, "thumb")} alt="" className="w-full h-full object-cover" />
                ) : it.media_type === "video" ? (
                  <Film size={16} className="text-gray-400" />
                ) : (
                  <ImageIcon size={16} className="text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#000000] truncate">
                  {it.title || "Untitled"}
                  {it.is_deleted && <span className="ml-2 text-xs text-gray-400">(deleted)</span>}
                </div>
                <div className="text-xs text-gray-400 capitalize">{it.media_type}</div>
              </div>
              <div className="font-display font-black text-xl text-[#000000] tabular-nums">{it.count}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState(null);

  useEffect(() => {
    setOverview(null);
    setClients(null);
    api
      .get("/admin/analytics/overview", { params: { range } })
      .then((r) => setOverview(r.data))
      .catch(() => setOverview({}));
    api
      .get("/admin/analytics/client-activity", { params: { range } })
      .then((r) => setClients(r.data.items || []))
      .catch(() => setClients([]));
  }, [range]);

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#000000]">Download Insights</h1>
          <p className="text-gray-500 mt-1 text-sm">See which ads clients love — and who's engaging most.</p>
        </div>
        <div className="flex gap-2" data-testid="analytics-range-filter">
          {RANGES.map((r) => (
            <button
              key={r.value}
              data-testid={`range-${r.value}`}
              onClick={() => setRange(r.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                range === r.value ? "bg-[#000000] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8" data-testid="analytics-overview-grid">
        {OVERVIEW_CARDS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="bg-white border border-gray-200 rounded-xl p-6" data-testid={`overview-${key}`}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
              <Icon size={17} style={{ color: color === "#D0FF0D" ? "#000000" : color }} />
            </div>
            <div className="font-display font-black text-3xl text-[#000000] mt-4 tabular-nums">
              {overview ? (overview[key] ?? 0) : "—"}
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        {METRICS.map((m) => (
          <TopAdsCard key={m.key} metric={m.key} label={m.label} icon={m.icon} color={m.color} range={range} />
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mt-8" data-testid="client-activity-card">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[#4A0A66]22" style={{ background: "#4A0A6622" }}>
            <Users size={15} style={{ color: "#4A0A66" }} />
          </div>
          <h3 className="font-display font-black text-lg text-[#000000]">Client Activity</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-[0.1em] text-gray-400 border-b border-gray-100">
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3 text-right">Views</th>
                <th className="px-6 py-3 text-right">Downloads</th>
                <th className="px-6 py-3 text-right">Favorites</th>
                <th className="px-6 py-3">Last active</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {clients === null ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-400" data-testid="client-activity-empty">No client activity yet.</td></tr>
              ) : (
                clients.map((c, i) => (
                  <tr key={c.user_id} className="border-b border-gray-50 last:border-0" data-testid={`client-row-${i}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-[#000000]">{c.name || c.email.split("@")[0]}</div>
                      <div className="text-xs text-gray-400">{c.email}</div>
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums">{c.views}</td>
                    <td className="px-6 py-3 text-right tabular-nums font-semibold">{c.downloads}</td>
                    <td className="px-6 py-3 text-right tabular-nums">{c.favorites}</td>
                    <td className="px-6 py-3 text-gray-600">{formatDate(c.last_active)}</td>
                    <td className="px-6 py-3">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        c.access_enabled ? "bg-[#D0FF0D22] text-[#000000]" : "bg-gray-100 text-gray-500"
                      }`}>
                        {c.access_enabled ? "Active" : "Disabled"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
